/**
 * KAN-757: чистые функции правки строки доски CRM в ячейке: оптимистичное
 * применение частичной правки, замена строки клиента во всей доске. KAN-840:
 * перевод даты следующей связи в поле datetime-local и обратно живёт в
 * lib/orgTimezone (по времени организации).
 */
import type { ManagerBrief } from '@/types/manager';
import type { CrmBoard, CrmFunnelStage, CrmRow, CrmRowPatch } from '@/types/crm';

/** Префикс ссылки Telegram, как на сервере (telegram_identity.normalize_username). */
const TME_PREFIX_RE = /^(?:https?:\/\/)?(?:t|telegram)\.me\//i;

/** "@ivan" / "https://t.me/ivan" / "t.me/ivan/" / " ivan " -> "ivan". */
export function normalizeTelegramUsername(value: string): string {
  return value.trim().replace(TME_PREFIX_RE, '').replace(/^@+/, '').trim().replace(/\/+$/, '');
}

/** Сохранённый username как его показывает поле: "@username", без него пусто. */
export function telegramUsernameInputValue(saved?: string | null): string {
  const username = normalizeTelegramUsername(saved ?? '');
  return username ? `@${username}` : '';
}

/**
 * Username для отправки ("" снимает его) или null, когда ввод после
 * нормализации совпадает с сохранённым. Регистр значим.
 */
export function telegramUsernameEdit(input: string, saved?: string | null): string | null {
  const next = normalizeTelegramUsername(input);
  return next === normalizeTelegramUsername(saved ?? '') ? null : next;
}

/** Строка с применённой правкой (до ответа сервера). KAN-760: этап из списка этапов доски. */
export function applyCrmPatch(
  row: CrmRow,
  patch: CrmRowPatch,
  managers: readonly ManagerBrief[],
  stages: readonly CrmFunnelStage[] = []
): CrmRow {
  const next: CrmRow = { ...row };
  if (patch.phone !== undefined) next.phone = patch.phone;
  if (patch.clinic !== undefined) next.clinic = patch.clinic;
  if (patch.city !== undefined) next.city = patch.city;
  if (patch.contact_name !== undefined) next.contact_name = patch.contact_name;
  if (patch.comment !== undefined) next.comment = patch.comment;
  if (patch.next_contact_at !== undefined) next.next_contact_at = patch.next_contact_at;
  if (patch.telegram_username !== undefined) {
    const username = normalizeTelegramUsername(patch.telegram_username);
    next.telegram = username ? { username, link: `https://t.me/${username}` } : null;
  }
  if (patch.manager_id !== undefined) {
    const manager = patch.manager_id ? managers.find((m) => m.id === patch.manager_id) : undefined;
    next.manager = manager ? { id: manager.id, name: manager.full_name } : null;
  }
  if (patch.stage_id !== undefined) {
    const stage = stages.find((s) => s.id === patch.stage_id);
    if (stage) next.stage = { id: stage.id, name: stage.name, codename: stage.codename };
  }
  // KAN-783: словарь атрибутов мержится по ключам; пустая строка снимает значение (как на сервере).
  if (patch.attributes !== undefined) {
    const merged: Record<string, string | null> = { ...(row.attributes ?? {}) };
    for (const [name, value] of Object.entries(patch.attributes)) merged[name] = value === '' ? null : value;
    next.attributes = merged;
  }
  return next;
}

/** Доска, где строка клиента заменена во внимании и во всех днях. */
export function replaceCrmRow(board: CrmBoard, clientId: string, update: (row: CrmRow) => CrmRow): CrmBoard {
  const map = (rows: CrmRow[]) => rows.map((r) => (r.client_id === clientId ? update(r) : r));
  return {
    ...board,
    attention: map(board.attention),
    days: board.days.map((day) => ({ ...day, rows: map(day.rows) })),
  };
}
